/**
 * ENG-2317 ULCM Feedback — post-login "your contact has been verified" notice.
 *
 * LoginStateManager writes to sessionStorage when the login response
 * carries `invite_contacts_verified`. This hook reads AND clears the
 * flag on first mount so the banner shows exactly once.
 *
 * Usage:
 *   const { justVerified } = useInviteContactsVerified();
 *   if (justVerified) {
 *     // render a one-time banner / toast-alternative
 *   }
 */

import { useEffect, useState } from 'react';

const STORAGE_KEY = 'ulcm.inviteContactsVerified';

interface UseInviteContactsVerifiedResult {
  justVerified: boolean;
  resolvedOtpIds: string[];
}

const readAndClear = (): string[] => {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return [];
    }
    window.sessionStorage.removeItem(STORAGE_KEY);
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      return parsed.filter((v): v is string => typeof v === 'string');
    }
    return [];
  } catch {
    return [];
  }
};

const useInviteContactsVerified = (): UseInviteContactsVerifiedResult => {
  const [resolvedOtpIds, setResolvedOtpIds] = useState<string[]>([]);
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    if (checked) {
      return;
    }
    setResolvedOtpIds(readAndClear());
    setChecked(true);
  }, [checked]);

  return {
    justVerified: resolvedOtpIds.length > 0,
    resolvedOtpIds,
  };
};

export default useInviteContactsVerified;
export { STORAGE_KEY as INVITE_CONTACTS_VERIFIED_STORAGE_KEY };
